import { useState } from "react";
import { Quote, Maximize2, MapPin, Star } from "lucide-react";

const PROJECTS = [
  {
    id: "coastal-bedroom",
    title: "Coastal Scallop Bedroom Nook",
    location: "Built-in Headboard Suite",
    tag: "Wide Angle",
    image:
      "https://customer-assets.emergentagent.com/job_portfolio-visual-13/artifacts/vwczssxi_IMG_0834.jpeg",
    description:
      "Sage scallop-shell wallpaper wrapped seamlessly around a custom shiplap headboard and floating nightstands for a serene coastal retreat.",
    testimonial: {
      quote:
        "The pattern alignment around our built-in headboard is flawless — it feels like the room was designed around the paper. Pure craftsmanship.",
      author: "Hamptons Homeowner",
      rating: 5,
    },
  },
  {
    id: "crane-powder-ceiling",
    title: "Flying Crane Powder Room",
    location: "Statement Ceiling Application",
    tag: "Detail",
    image:
      "https://customer-assets.emergentagent.com/job_portfolio-visual-13/artifacts/7w33eeaj_IMG_0831.jpeg",
    description:
      "A soft-blue crane mural carried up the walls and across the ceiling, with razor-clean seams framing the chandelier medallion.",
    testimonial: {
      quote:
        "They wrapped the ceiling and matched every seam around the light fixture. Guests literally gasp when they walk in.",
      author: "Interior Design Partner",
      rating: 5,
    },
  },
  {
    id: "equestrian-powder",
    title: "Equestrian Toile Powder Room",
    location: "Full-Height Wainscot Install",
    tag: "Wide Angle",
    image:
      "https://customer-assets.emergentagent.com/job_portfolio-visual-13/artifacts/zla3matq_IMG_0830.jpeg",
    description:
      "Classic green equestrian toile installed above tiled wainscoting, with perfect horizontal repeats wrapping all four corners.",
    testimonial: {
      quote:
        "Corners and the trim transitions are immaculate. This is the third room they've papered for us — never anyone else.",
      author: "Returning Client",
      rating: 5,
    },
  },
  {
    id: "dragon-ceiling",
    title: "Teal Dragon Statement Ceiling",
    location: "Sloped Ceiling Feature",
    tag: "Statement Ceiling",
    image: "/portfolio/project4.jpg",
    description:
      "A bold teal dragon and tropical-foliage print applied to an angled bathroom ceiling — a maximalist showpiece executed with surgical precision.",
    testimonial: {
      quote:
        "An angled ceiling with a giant repeating motif sounds impossible. They made it look effortless and the result is jaw-dropping.",
      author: "Design-Build Client",
      rating: 5,
    },
  },
  {
    id: "botanical-hall",
    title: "Botanical Green Entry Hall",
    location: "Heritage Floral Transformation",
    tag: "Transformation",
    image: "/portfolio/project5.jpg",
    description:
      "Dense heritage florals on a deep green ground, color-matched to a freshly lacquered door for an immersive, jewel-box entryway.",
    testimonial: {
      quote:
        "They transformed a plain hallway into the most photographed corner of our home. Meticulous from prep to final cut.",
      author: "Private Residence",
      rating: 5,
    },
  },
  {
    id: "botanical-dining",
    title: "Grey Botanical Dining Room",
    location: "Coffered Ceiling Suite",
    tag: "Wide Angle",
    image: "/portfolio/project6.jpg",
    description:
      "A delicate grey branch-and-botanical print on a soft cream ground, installed to complement a layered coffered ceiling for an airy, elegant dining space.",
    testimonial: {
      quote:
        "The pattern flows perfectly into the ceiling detail. The whole room finally feels finished and refined.",
      author: "Greenwich Homeowner",
      rating: 5,
    },
  },
  {
    id: "heron-mural",
    title: "Heritage Heron Mural",
    location: "Scenic Entry Feature Wall",
    tag: "Statement Mural",
    image: "/portfolio/project7.jpg",
    description:
      "A large-scale antique Louisiana Heron mural in muted blues, greens, and sepia, hung as a panoramic scenic feature with seamless panel alignment.",
    testimonial: {
      quote:
        "Matching a panoramic mural across panels is an art form. Every transition is invisible — true masters.",
      author: "Design Studio Collaborator",
      rating: 5,
    },
  },
  {
    id: "charcoal-powder",
    title: "Charcoal Grasscloth Powder Room",
    location: "Textured Wallcovering",
    tag: "Detail",
    image: "/portfolio/project8.jpg",
    description:
      "Deep charcoal textured grasscloth paired with marble and brass — a moody, sophisticated powder room with flawless seam placement around fixtures.",
    testimonial: {
      quote:
        "Grasscloth is unforgiving, but the seams are imperceptible. The room feels like a luxury hotel suite.",
      author: "Boutique Hotelier",
      rating: 5,
    },
  },
  {
    id: "floral-mudroom",
    title: "Diamond Floral Mudroom",
    location: "Wrapped Column Install",
    tag: "Wide Angle",
    image: "/portfolio/project9.jpg",
    description:
      "A cheerful green-and-white diamond floral wrapped cleanly around a structural column and adjoining walls, turning a utility space into a delight.",
    testimonial: {
      quote:
        "Wrapping the column with a continuous pattern looked impossible. They nailed every corner — we smile every time we walk in.",
      author: "Family Residence",
      rating: 5,
    },
  },
  {
    id: "blush-ceiling-powder",
    title: "Charcoal & Blush Powder Room",
    location: "Two-Tone Walls + Ceiling",
    tag: "Statement Ceiling",
    image: "/portfolio/project10.jpg",
    description:
      "Dramatic charcoal textured walls meet a soft blush textured ceiling — a bold two-surface treatment executed cleanly across an angled ceiling line.",
    testimonial: {
      quote:
        "Papering both the walls and that sloped ceiling in two textures was ambitious. The result is pure drama, done flawlessly.",
      author: "Interior Designer",
      rating: 5,
    },
  },
];

const VIDEOS = [
  {
    id: "reel-1",
    title: "Installation Reel — Pattern Match",
    src: "https://customer-assets.emergentagent.com/job_portfolio-visual-13/artifacts/z4t85psf_v15044gf0000d1c6ggvog65ju780qjvg.mp4",
  },
  {
    id: "reel-2",
    title: "Behind the Craft — Seam Work",
    src: "https://customer-assets.emergentagent.com/job_portfolio-visual-13/artifacts/qv4c9e4j_v15044gf0000d7vjck7og65t3td5bdb0.mp4",
  },
  {
    id: "reel-3",
    title: "On-Site Walkthrough",
    src: "https://customer-assets.emergentagent.com/job_portfolio-visual-13/artifacts/n6myew1z_v15044gf0000d7vj7avog65kas9hagn0.mp4",
  },
  {
    id: "reel-4",
    title: "Detail in Motion",
    src: "https://customer-assets.emergentagent.com/job_portfolio-visual-13/artifacts/a4oi16av_v15044gf0000d4gkbtnog65sl1slgobg.mp4",
  },
  {
    id: "reel-5",
    title: "Finished Reveal",
    src: "https://customer-assets.emergentagent.com/job_portfolio-visual-13/artifacts/hp6135la_v15044gf0000d5vrrf7og65rcuha8mk0.mp4",
  },
  {
    id: "reel-6",
    title: "Precision in Progress",
    src: "https://customer-assets.emergentagent.com/job_portfolio-visual-13/artifacts/jwezkywo_v15044gf0000d8gd717og65qh517sr00.mp4",
  },
  {
    id: "reel-7",
    title: "Final Walkthrough",
    src: "https://customer-assets.emergentagent.com/job_portfolio-visual-13/artifacts/4r1ansks_v15044gf0000d82e0d7og65oimva3q50.mp4",
  },
];

export const ProjectsPage = ({ themeConfig }) => {
  const [lightbox, setLightbox] = useState(null);

  return (
    <div className="animate-fade-in" data-testid="projects-page">
      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex flex-col justify-center items-center p-6 animate-fade-in"
          onClick={() => setLightbox(null)}
        >
          <button
            data-testid="project-lightbox-close"
            onClick={() => setLightbox(null)}
            className="absolute top-6 right-6 text-white hover:text-neutral-300 font-mono text-xs uppercase tracking-widest"
          >
            [ Close × ]
          </button>
          <img
            src={lightbox.image}
            alt={lightbox.title}
            className="max-h-[78vh] max-w-full object-contain border border-neutral-800"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="mt-5 text-center">
            <h3 className="text-white text-lg font-semibold">{lightbox.title}</h3>
            <p className="text-neutral-400 text-xs uppercase tracking-widest mt-1">
              {lightbox.location}
            </p>
          </div>
        </div>
      )}

      {/* Intro */}
      <section className="mb-14 md:mb-20 text-left">
        <span className="text-xs font-bold uppercase tracking-widest opacity-60 block mb-3">
          SELECTED INSTALLATIONS
        </span>
        <h1
          className={`text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight mb-4 ${themeConfig.fontFamilyHeading}`}
        >
          Our Work
        </h1>
        <p className="text-base sm:text-lg max-w-2xl opacity-75 font-light leading-relaxed">
          A curated look at recent installations &mdash; from wide-angle room
          transformations to dramatic statement ceilings and the close-up
          details that define true craftsmanship.
        </p>
      </section>

      {/* Responsive grid of projects + testimonials */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 items-start">
        {PROJECTS.map((project) => (
          <article
            key={project.id}
            data-testid={`project-card-${project.id}`}
            className={`group ${themeConfig.cardClass} overflow-hidden flex flex-col`}
          >
            {/* Image */}
            <div className="relative overflow-hidden bg-neutral-100 aspect-[4/5]">
              <img
                src={project.image}
                alt={project.title}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
              />
              <span className="absolute top-3 left-3 bg-black/75 backdrop-blur-sm text-[10px] text-white font-mono px-2.5 py-1 uppercase tracking-widest font-semibold">
                {project.tag}
              </span>
              <button
                data-testid={`project-expand-${project.id}`}
                onClick={() => setLightbox(project)}
                className="absolute top-3 right-3 p-2 bg-black/60 hover:bg-black/90 text-white opacity-0 group-hover:opacity-100 transition-all"
                title="View full screen"
              >
                <Maximize2 className="w-4 h-4" />
              </button>
            </div>

            {/* Meta */}
            <div className="p-6 flex flex-col flex-1">
              <h3
                className={`text-xl font-medium tracking-tight mb-1.5 ${themeConfig.fontFamilyHeading}`}
              >
                {project.title}
              </h3>
              <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest opacity-55 font-semibold mb-3">
                <MapPin className="w-3 h-3" />
                {project.location}
              </p>
              <p className="text-sm opacity-70 leading-relaxed font-light mb-5">
                {project.description}
              </p>

              {/* Testimonial */}
              <div className={`mt-auto p-4 ${themeConfig.surface} ${themeConfig.border} border relative`}>
                <Quote className="w-5 h-5 opacity-20 mb-2" />
                <p className="text-sm italic leading-relaxed font-light mb-3">
                  &ldquo;{project.testimonial.quote}&rdquo;
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-[11px] uppercase tracking-widest font-semibold opacity-70">
                    {project.testimonial.author}
                  </span>
                  <span className="flex gap-0.5">
                    {Array.from({ length: project.testimonial.rating }).map((_, i) => (
                      <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />
                    ))}
                  </span>
                </div>
              </div>
            </div>
          </article>
        ))}
      </section>

      {/* Video Walkthroughs */}
      <section className="mt-20 border-t border-black/5 pt-14" data-testid="video-section">
        <div className="mb-10 max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-widest opacity-60 block mb-3">
            IN MOTION
          </span>
          <h2
            className={`text-2xl sm:text-3xl font-light tracking-tight mb-3 ${themeConfig.fontFamilyHeading}`}
          >
            Video Walkthroughs
          </h2>
          <p className="text-sm opacity-70 font-light leading-relaxed">
            Short reels from the field &mdash; see the precision, seam-matching,
            and finished reveals up close.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
          {VIDEOS.map((video) => (
            <div
              key={video.id}
              data-testid={`video-card-${video.id}`}
              className={`group ${themeConfig.cardClass} overflow-hidden`}
            >
              <div className="relative bg-black aspect-[9/16]">
                <video
                  src={video.src}
                  controls
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  className="w-full h-full object-cover"
                />
              </div>
              <p className="px-3 py-3 text-[11px] uppercase tracking-widest font-semibold opacity-70 leading-snug">
                {video.title}
              </p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};
